import React, { useRef, useEffect, useMemo, useState } from 'react';
import Chart from 'chart.js/auto';

const ExpensesTimeSeriesChart = ({ 
    transactions, 
    budgets = [],
    negateForExpenses = false, 
    enforceExpenseSign = false, 
    excludeTransfers = true, 
    excludedCategories = [], 
    categoryHierarchy = [], 
    startDate: propsStartDate = null, 
    endDate: propsEndDate = null, 
    onSelectCategory = null, 
    onSelectPeriods = null, 
    selectedCategory = null, 
    selectedPeriods = [], 
    onPeriodCountChange = null,
    excludeDebtPayments: propsExcludeDebtPayments = false,
    onExcludeDebtPaymentsChange = null
}) => {
    const canvasRef = useRef(null);
    const chartRef = useRef(null);
    // Refs to hold latest prop values so the chart onClick closure never goes stale
    const selectedPeriodsRef = useRef(selectedPeriods);
    const selectedCategoryRef = useRef(selectedCategory);
    const onSelectPeriodsRef = useRef(onSelectPeriods);
    const onSelectCategoryRef = useRef(onSelectCategory);
    useEffect(() => { selectedPeriodsRef.current = selectedPeriods; }, [selectedPeriods]);
    useEffect(() => { selectedCategoryRef.current = selectedCategory; }, [selectedCategory]);
    useEffect(() => { onSelectPeriodsRef.current = onSelectPeriods; }, [onSelectPeriods]);
    useEffect(() => { onSelectCategoryRef.current = onSelectCategory; }, [onSelectCategory]);
    const [grouping, setGrouping] = useState('daily');
    const [showCategories, setShowCategories] = useState(false);
    const [isCumulative, setIsCumulative] = useState(false);
    const [excludeDebtPayments, setExcludeDebtPayments] = useState(propsExcludeDebtPayments);

    const [showSubCategories, setShowSubCategories] = useState(false);

    // Sync excludeDebtPayments with parent when it changes externally
    useEffect(() => { setExcludeDebtPayments(propsExcludeDebtPayments); }, [propsExcludeDebtPayments]);

    const toMonthly = (amount, freq) => {
        const amt = parseFloat(amount);
        if (isNaN(amt) || amt === 0) return 0.0;

        const count = parseInt(freq?.count || 1);
        const unit = String(freq?.unit || 'months').toLowerCase();
        
        const WEEKS_PER_MONTH = 4.0;
        const DAYS_PER_MONTH = 30.0;

        if (unit === 'days') return amt * (DAYS_PER_MONTH / count);
        if (unit === 'weeks') return amt * (WEEKS_PER_MONTH / count);
        if (unit === 'months') return amt * (1.0 / count);
        if (unit === 'years') return amt * (1.0 / (12.0 * count));
        
        return amt * (1.0 / (count > 0 ? count : 1));
    };

    const renderDiffLabels = (chart) => {
        const ctx = chart.ctx;

        ctx.save();
        ctx.textAlign = 'center';
        ctx.textBaseline = 'bottom';
        ctx.font = 'bold 11px sans-serif';

        const datasets = chart.data.datasets;
        const labels = chart.data.labels;

        labels.forEach((label, i) => {
            let actualTotal = 0;
            let budgetTotal = 0;
            let lastActualMeta = null;
            let lastBudgetMeta = null;

            let hasActualVisible = false;
            let hasBudgetVisible = false;

            datasets.forEach((dataset, dsIndex) => {
                const meta = chart.getDatasetMeta(dsIndex);
                if (meta.hidden) return;

                if (dataset.stack === 'Actual' || (!dataset.stack && dsIndex === 0)) {
                    actualTotal += dataset.data[i] || 0;
                    lastActualMeta = meta;
                    hasActualVisible = true;
                } else if (dataset.stack === 'Budget') {
                    budgetTotal += dataset.data[i] || 0;
                    lastBudgetMeta = meta;
                    hasBudgetVisible = true;
                }
            });

            if (hasActualVisible && hasBudgetVisible && lastActualMeta && lastBudgetMeta) {
                const actualModel = lastActualMeta.data[i];
                const budgetModel = lastBudgetMeta.data[i];

                if (actualModel && budgetModel) {
                    const diff = actualTotal - budgetTotal;
                    if (Math.abs(diff) < 0.01) return; // Ignore tiny differences

                    const formattedDiff = (diff >= 0 ? '+' : '') + new Intl.NumberFormat('en-US', { 
                        style: 'currency', 
                        currency: 'USD',
                        maximumFractionDigits: 0
                    }).format(diff);

                    const xActual = actualModel.x;
                    const xBudget = budgetModel.x;
                    const yActual = actualModel.y;
                    const yBudget = budgetModel.y;

                    const xMid = (xActual + xBudget) / 2;
                    const color = diff > 0 ? '#ef4444' : '#10b981';
                    
                    ctx.fillStyle = color;
                    ctx.strokeStyle = color;
                    ctx.lineWidth = 1;

                    // Draw indicator line between the tops of the two bars if they have different heights
                    const yMin = Math.min(yActual, yBudget);
                    const yMax = Math.max(yActual, yBudget);
                    
                    // Vertical line indicating the difference
                    ctx.beginPath();
                    ctx.moveTo(xMid, yMin);
                    ctx.lineTo(xMid, yMax);
                    ctx.stroke();

                    // Horizontal ticks at top and bottom of difference
                    const tickWidth = 6;
                    ctx.beginPath();
                    ctx.moveTo(xMid - tickWidth/2, yMin);
                    ctx.lineTo(xMid + tickWidth/2, yMin);
                    ctx.moveTo(xMid - tickWidth/2, yMax);
                    ctx.lineTo(xMid + tickWidth/2, yMax);
                    ctx.stroke();

                    // Text label
                    ctx.fillText(formattedDiff, xMid, yMin - 5);
                }
            }
        });
        ctx.restore();
    };

    // Color palette for categories (matched with TransactionsPieChart.js)
    const categoryColors = useMemo(() => ({
        'food_dining': '#FF6B35',
        'gas_fuel': '#7B1FA2',
        'groceries': '#2E7D32',
        'internet': '#1565C0',
        'apps': '#5E35B1',
        'books': '#fbbf24',
        'landscaping': '#388E3C',
        'shopping': '#C2185B',
        'entertainment': '#0277BD',
        'travel': '#F57C00',
        'automotive': '#00695C',
        'healthcare': '#C62828',
        'utilities': '#EF6C00',
        'subscription': '#8E24AA',
        'insurance': '#3F51B5',
        'taxes': '#B71C1C',
        'kids': '#22c55e',
        'education': '#0097A7',
        'business': '#455A64',
        'personal_care': '#AD1457',
        'home': '#8b5cf6',
        'home_improvement': '#558B2F',
        'gifts_charity': '#9E9D24',
        'fees_charges': '#D32F2F',
        'transfer': '#1976D2',
        'payments': '#2E7D32',
        'rewards_cashback': '#F57C00',
        'waste_management': '#10b981',
        'sanitation': '#10b981',
        'waste_management__recycling': '#34d399',
        'waste_management__yard_waste': '#059669',
        'waste_management__other': '#6ee7b7',
        'utilities__other': '#93c5fd',
        'tv_cellphones': '#f472b6',
        'tv': '#7c3aed',
        'cellphones': '#f472b6',
        'media': '#2563eb',
        'gym_memberships_fees': '#84cc16',
        'supplements': '#a3e635',
        'hair_care': '#f9a8d4',
        'nails': '#fb7185',
        'personal_care__other': '#fecaca',
        'dental_products': '#ffe4e6',
        'workout_gym_products': '#bef264',
        'other': '#616161',
        'none': '#9ca3af'
    }), []);

    const getCategoryLabel = useMemo(() => {
        return (categoryValue) => {
            // Build label map from categoryHierarchy
            const labelMap = new Map();
            if (Array.isArray(categoryHierarchy)) {
                categoryHierarchy.forEach(parent => {
                    labelMap.set(parent.value, parent.label);
                    if (parent.children && Array.isArray(parent.children)) {
                        parent.children.forEach(child => {
                            labelMap.set(child.value, child.label);
                        });
                    }
                });
            }

            const toTitle = (slug) => {
                if (!slug) return '';
                // Remove leading underscores
                let s = slug.replace(/^_+/, '');
                // Replace remaining underscores with spaces
                s = s.replace(/_+/g, ' ');
                // Convert to Title Case
                return s.split(' ')
                    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                    .join(' ');
            };

            // Support composite keys like "parent|child" to render "Parent - Child"
            if (typeof categoryValue === 'string' && categoryValue.includes('|')) {
                const [p, c] = categoryValue.split('|');
                let pl = getCategoryLabel(p);
                let cl = getCategoryLabel(c);
                
                // Clean up duplications like "Media - Media (Other)" -> "Media - Other"
                if (cl.toLowerCase().startsWith(pl.toLowerCase())) {
                    const remaining = cl.slice(pl.length).trim();
                    // Remove leading dashes or spaces
                    cl = remaining.replace(/^[:\-\s]+/, '');
                    // Ensure first letter is capitalized
                    cl = cl.charAt(0).toUpperCase() + cl.slice(1);
                } else {
                    // If not starting with parent name, still apply the formatting requested
                    // (though getCategoryLabel(c) should have already done some formatting if it hit the fallback)
                    // However, cl might have come from categoryMap, so we might not want to force toTitle if it's already mapped.
                }
                return `${pl} - ${cl}`;
            }
            const categoryMap = {
                'transfer': 'Transfer',
                'payments': 'Payments',
                'rewards_cashback': 'Rewards/Cashback',
                'other': 'Other',
                'none': 'Uncategorized',
            };
            return labelMap.get(categoryValue) || categoryMap[categoryValue] || toTitle(categoryValue);
        };
    }, [categoryHierarchy]);

    const childToParent = useMemo(() => {
        const map = new Map();
        if (Array.isArray(categoryHierarchy)) {
            categoryHierarchy.forEach(parent => {
                if (parent.children && Array.isArray(parent.children)) {
                    parent.children.forEach(child => {
                        map.set(child.value, parent.value);
                    });
                }
            });
        }
        return (val) => {
            if (map.has(val)) return map.get(val);
            // Legacy fallbacks if hierarchy is missing or if category is not in hierarchy but follows naming convention
            const HOME_CHILDREN = new Set(['home_repair','home_maintenance','home_enhancement','home_decor','home_furniture','home_artwork','home_pest_control','home_other','home_improvement','home_inspection','home_tax_payment','home_insurance_payment', 'home_plants']);
            const UTILITIES_CHILDREN = new Set(['water','gas','electric','electricity','electric_gas','utilities__other']);
            const MEDIA_CHILDREN = new Set(['apps','internet','tv','books','cellphones','media__other','media_other','tv_cellphones','media_entertainment']);
            const PERSONAL_CARE_CHILDREN = new Set(['gym_memberships_fees','supplements','hair_care','nails','personal_care__other','skin_care_products','physical_therapy','personal_care_other','prescriptions','medicines','make_up','skin_care_treatments','spa_massage_sauna','dental_products', 'workout_gym_products']);
            const WASTE_CHILDREN = new Set(['sanitation','waste_management__recycling','waste_management__yard_waste','waste_management__other']);
            const KIDS_CHILDREN = new Set(['kids_clothing','kids_sports_league_fees','kids_sports_equipment_clothing','kids_dance_fees','kids_dance_clothing','kids_school_meals','kids_shopping','kids_camp_tuition_fees','kids__other','kids_other','kids_bday_parties','kids_activities']);

            if (HOME_CHILDREN.has(val)) return 'home';
            if (UTILITIES_CHILDREN.has(val)) return 'utilities';
            if (MEDIA_CHILDREN.has(val)) return 'media';
            if (PERSONAL_CARE_CHILDREN.has(val)) return 'personal_care';
            if (WASTE_CHILDREN.has(val)) return 'waste_management';
            if (KIDS_CHILDREN.has(val)) return 'kids';
            
            // Debt Payment relationships
            if (val === 'financing_interest' || val === 'financing_principal') return 'payments';

            // Special case for vehicle licensing tags if it should be under automotive
            if (val === 'vehicle_licensing_tags') return 'automotive';

            return null;
        };
    }, [categoryHierarchy]);

    const chartData = useMemo(() => {
        if (!transactions || transactions.length === 0) return { labels: [], datasets: [] };

        const dataPoints = {}; // { period: { total: 0, categories: { cat: 0 } } }
        const categoriesSet = new Set();

        // Filter by date range before computing periods
        const filteredTransactions = transactions.filter(t => {
            // Avoid double-counting split transactions
            const hasChildren = !!(t.children_count || (t.has_children === true) || (t.children && t.children.length > 0));
            const rawCat = (t.expense_category || 'none').toLowerCase();
            const linkedType = t.revolver_type || t.linked_debt_info?.type || null;
            // Exclude CC-linked payments; include mortgage/auto/HELOC and unlinked cash payments
            const isCCPayment = (rawCat === 'payments') && (linkedType === 'credit_card');

            // Exclude child transactions from the graph to avoid double-counting with their parent.
            // Only parent transactions (or standalone transactions with no parent) should appear in the graph.
            if (t.parent != null) return false;

            if (hasChildren && isCCPayment) return false;

            // Exclude non-CC debt payments (mortgage, auto_loan, HELOC) when toggle is on
            if (excludeDebtPayments && rawCat === 'payments' && linkedType !== 'credit_card') return false;

            const rawDateStr = t.date || t.transaction_date;
            const datePart = rawDateStr ? rawDateStr.split('T')[0] : null;
            if (!datePart) return false;
            const date = new Date(datePart + 'T00:00:00');
            if (isNaN(date.getTime())) return false;

            const startStr = propsStartDate || (t.startDate ? t.startDate : null);
            const endStr = propsEndDate || (t.endDate ? t.endDate : null);

            if (startStr) {
                const s = new Date(startStr.split('T')[0] + 'T00:00:00');
                if (!isNaN(s.getTime()) && date < s) return false;
            }
            if (endStr) {
                const e = new Date(endStr.split('T')[0] + 'T00:00:00');
                if (!isNaN(e.getTime()) && date > e) return false;
            }
            return true;
        });

        // Deduplicate: Plaid sometimes returns two records for the same payment — one with the raw
        // ACH description and one with a cleaned merchant name. Keep the "richer" record:
        // prefer the one with children/splits (score 2), then linked fields (score 1), then lowest id.
        const _hasLinked = t => !!(
            t.linked_debt_id || t.linked_debt_txn_id ||
            t.linked_asset_id || t.linked_asset_expense_id ||
            t.linked_utility_bill_id || t.linked_recurring_series_id ||
            t.linked_income_payment_id || t.linked_income_stream_id ||
            t.linkedDebtId || t.linkedAssetId || t.linkedUtilityBillId || t.linkedRecurringSeriesId
        );
        const _hasKids = t => !!(t.children_count || (t.has_children === true) || (t.children && t.children.length > 0));
        const _bestByKey = new Map();
        for (const t of filteredTransactions) {
            const key = `${(t.date || t.transaction_date || '').split('T')[0]}|${parseFloat(t.amount || 0)}`;
            if (!_bestByKey.has(key)) {
                _bestByKey.set(key, t);
            } else {
                const existing = _bestByKey.get(key);
                const tScore = (_hasKids(t) ? 2 : 0) + (_hasLinked(t) ? 1 : 0);
                const eScore = (_hasKids(existing) ? 2 : 0) + (_hasLinked(existing) ? 1 : 0);
                if (tScore > eScore || (tScore === eScore && (t.id || 0) < (existing.id || 0))) {
                    _bestByKey.set(key, t);
                }
            }
        }
        const dedupedTransactions = filteredTransactions.filter(t => {
            const key = `${(t.date || t.transaction_date || '').split('T')[0]}|${parseFloat(t.amount || 0)}`;
            return _bestByKey.get(key) === t;
        });

        dedupedTransactions.forEach(t => {
            let amt = parseFloat(t.amount);
            if (isNaN(amt)) amt = 0;
            
            // Align with Income Statement: Filter by sign to exclude credits/deposits from expenses
            const isCash = !t.revolver_type && !t.revolver_id;
            const isCC = !!(t.revolver_type || t.revolver_id);
            const rawCat = t.expense_category || 'none';

            let isSpend = false;
            if (isCash && amt < 0) isSpend = true;
            if (isCC && amt > 0) isSpend = true;
            // HELOC payments are considered spend when they are a debit to cash (handled in cash) 
            // but also if they are a payment on the HELOC (negative amount on CC side increases balance)
            // Note: HELOC revolver-side transactions are excluded below in the payments filter;
            // do NOT mark them as spend here to avoid double-counting with the cash account debit.

            // ALSO allow credits/refunds if they are in an expense category (they reduce overall spend)
            let isRefund = false;
            if (!isSpend) {
                if (isCash && amt > 0 && !excludedCategories.includes(rawCat) && rawCat !== 'transfer' && rawCat !== 'rewards_cashback') {
                    isSpend = true;
                    isRefund = true;
                }
                if (isCC && amt < 0 && !excludedCategories.includes(rawCat) && rawCat !== 'transfer' && rawCat !== 'rewards_cashback') {
                    isSpend = true;
                    isRefund = true;
                }
                // ALSO check if it's a HELOC refund (positive CC amount)
                if (!isSpend && rawCat === 'payments' && t.revolver_type === 'heloc' && amt > 0) {
                    isSpend = true;
                    isRefund = true;
                }
            }

            if (!isSpend) return;

            if (enforceExpenseSign) {
                if (negateForExpenses) {
                    if (!(amt < 0)) return;
                } else {
                    if (!(amt > 0)) return;
                }
            }
            if (negateForExpenses) amt = -amt;

            if (excludedCategories.includes(rawCat)) return;
            if (excludeTransfers && (rawCat === 'transfer' || rawCat === 'rewards_cashback')) return;
            if (excludeTransfers && rawCat === 'payments') {
                const linkedType = t.revolver_type || t.linked_debt_info?.type || null;
                // Match table logic: exclude CC payments and HELOC revolver-side transactions;
                // include unlinked cash payments, mortgage, auto_loan payments
                if (linkedType === 'credit_card') return;
                if (linkedType === 'heloc' && (t.revolver_type === 'heloc' || t.revolver_id)) return;
                if (!linkedType && (t.revolver_type || t.revolver_id)) return;
                // Exclude non-CC debt payments when toggle is on
                if (excludeDebtPayments) return;
            }

            let cat = rawCat;
            const parent = childToParent(rawCat);
            if (!showSubCategories) {
                if (parent) cat = parent;
                if (rawCat === 'home_improvement') cat = 'home';
            } else {
                if (rawCat === 'home_improvement') {
                    cat = 'home|home_improvement';
                } else if (parent && parent !== rawCat) {
                    cat = `${parent}|${rawCat}`;
                }
            }
            
            // Special handling for loan payments mapping to 'payments' category if they have subcategories or specific child tags
            if (rawCat === 'payments' || parent === 'payments') {
                cat = showSubCategories ? 'payments|payments' : 'payments';
            }
            
            categoriesSet.add(cat);

            const rawDateStr = t.date || t.transaction_date;
            const datePart = rawDateStr ? rawDateStr.split('T')[0] : null;
            if (!datePart) return;
            const date = new Date(datePart + 'T00:00:00');
            if (isNaN(date.getTime())) return;

            let period;
            if (grouping === 'daily') {
                period = datePart;
            } else if (grouping === 'weekly') {
                const d = new Date(date);
                const day = d.getDay();
                const diff = d.getDate() - day + (day === 0 ? -6 : 1); // adjust when day is sunday
                const monday = new Date(d.setDate(diff));
                const yyyy = monday.getFullYear();
                const mm = String(monday.getMonth() + 1).padStart(2, '0');
                const dd = String(monday.getDate()).padStart(2, '0');
                period = `Week of ${yyyy}-${mm}-${dd}`;
            } else if (grouping === 'monthly') {
                const yyyy = date.getFullYear();
                const mm = String(date.getMonth() + 1).padStart(2, '0');
                period = `${yyyy}-${mm}`;
            } else if (grouping === 'quarterly') {
                const yyyy = date.getFullYear();
                const q = Math.floor(date.getMonth() / 3) + 1;
                period = `${yyyy} Q${q}`;
            } else if (grouping === 'annually') {
                period = `${date.getFullYear()}`;
            }

            if (!dataPoints[period]) {
                dataPoints[period] = { total: 0, categories: {} };
            }

            const contribution = isRefund ? -Math.abs(amt) : Math.abs(amt);
            dataPoints[period].total += contribution;
            if (!dataPoints[period].categories[cat]) dataPoints[period].categories[cat] = 0;
            dataPoints[period].categories[cat] += contribution;
        });

        let sortedPeriods = Object.keys(dataPoints).sort();

        // Sort budgets by start_date descending once to ensure we pick the most recent budget when multiple overlap
        const sortedBudgets = [...budgets].sort((a, b) => new Date(b.start_date) - new Date(a.start_date));

        // Calculate Budget Data Points
        const budgetDataPoints = {}; // { period: { total: 0, categories: { cat: 0 } } }
        sortedPeriods.forEach(period => {
            budgetDataPoints[period] = { total: 0, categories: {} };

            // Determine start and end date for this period
            let pStart, pEnd;
            if (grouping === 'daily') {
                pStart = new Date(period + 'T00:00:00');
                pEnd = new Date(period + 'T23:59:59');
            } else if (grouping === 'weekly') {
                const dateStr = period.replace('Week of ', '');
                pStart = new Date(dateStr + 'T00:00:00');
                pEnd = new Date(pStart);
                pEnd.setDate(pStart.getDate() + 6);
                pEnd.setHours(23, 59, 59);
            } else if (grouping === 'monthly') {
                const [y, m] = period.split('-').map(Number);
                pStart = new Date(y, m - 1, 1);
                pEnd = new Date(y, m, 0, 23, 59, 59);
            } else if (grouping === 'quarterly') {
                const [y, qStr] = period.split(' Q');
                const q = parseInt(qStr);
                pStart = new Date(parseInt(y), (q - 1) * 3, 1);
                pEnd = new Date(parseInt(y), q * 3, 0, 23, 59, 59);
            } else if (grouping === 'annually') {
                const y = parseInt(period);
                pStart = new Date(y, 0, 1);
                pEnd = new Date(y, 11, 31, 23, 59, 59);
            }

            // For each month in this period, find active budget and add up amounts
            let currMonth = new Date(pStart.getFullYear(), pStart.getMonth(), 1);
            const stopMonth = new Date(pEnd.getFullYear(), pEnd.getMonth(), 1);

            while (currMonth <= stopMonth) {
                const y = currMonth.getFullYear();
                const m = currMonth.getMonth(); // 0-indexed
                const monthStr = `${y}-${String(m + 1).padStart(2, '0')}`;
                
                const monthStart = new Date(y, m, 1);
                const monthEnd = new Date(y, m + 1, 0, 23, 59, 59);

                // Find budget active for this specific month (pick the most recent one among overlaps)
                const activeB = sortedBudgets.find(b => {
                    const bs = new Date(b.start_date);
                    const be = b.end_date ? new Date(b.end_date) : null;
                    return bs <= monthEnd && (be === null || be >= monthStart);
                });

                if (activeB) {
                    const rawOverrides = activeB.category_monthly_overrides || {};
                    const rawFixed = activeB.category_amounts || {};
                    const rawFreqs = activeB.category_frequencies || {};
                    
                    // Calculate monthly budget for ALL categories first to handle hierarchy correctly
                    const monthlyBudgets = {};
                    const keys = new Set([...Object.keys(rawFixed), ...Object.keys(rawOverrides)]);
                    keys.forEach(k => {
                        if (excludedCategories.includes(k)) return;
                        if (excludeTransfers && (k === 'transfer' || k === 'rewards_cashback')) return;
                        if (excludeDebtPayments && k === 'payments') return;
                        
                        let amt = 0;
                        const overrideVal = rawOverrides[k]?.[monthStr];
                        if (overrideVal !== undefined && overrideVal !== null) {
                            amt = parseFloat(overrideVal);
                        } else if (rawFixed[k] !== undefined) {
                            amt = toMonthly(rawFixed[k], rawFreqs[k]);
                        }
                        if (!isNaN(amt)) {
                            monthlyBudgets[k] = amt;
                        }
                    });

                    // Sum hierarchically to match backend logic and avoid double counting
                    const allChildren = new Set();
                    const parentToChildren = new Map();
                    
                    if (Array.isArray(categoryHierarchy)) {
                        categoryHierarchy.forEach(p => {
                            if (p.children && Array.isArray(p.children) && p.children.length > 0) {
                                const kids = p.children.map(ch => ch.value);
                                parentToChildren.set(p.value, kids);
                                kids.forEach(k => allChildren.add(k));
                            }
                        });
                    }

                    // Also include hardcoded relationships from childToParent if not in hierarchy
                    const budgetKeys = Object.keys(monthlyBudgets);
                    budgetKeys.forEach(k => {
                        const p = childToParent(k);
                        if (p && p !== k) {
                            allChildren.add(k);
                            if (!parentToChildren.has(p)) {
                                parentToChildren.set(p, []);
                            }
                            if (!parentToChildren.get(p).includes(k)) {
                                parentToChildren.get(p).push(k);
                            }
                        }
                    });

                    // Process all keys in monthlyBudgets
                    budgetKeys.forEach(k => {
                        const amt = monthlyBudgets[k] || 0;
                        if (amt === 0) return;

                        const p = childToParent(k);
                        let catKey = k;

                        if (showCategories) {
                            if (!showSubCategories) {
                                if (p) catKey = p;
                                if (k === 'home_improvement') catKey = 'home';
                            } else {
                                if (k === 'home_improvement') {
                                    catKey = 'home|home_improvement';
                                } else if (p && p !== k) {
                                    catKey = `${p}|${k}`;
                                }
                            }
                            
                            if (!budgetDataPoints[period].categories[catKey]) budgetDataPoints[period].categories[catKey] = 0;
                            budgetDataPoints[period].categories[catKey] += amt;
                            categoriesSet.add(catKey);
                        }

                        if (!allChildren.has(k)) {
                            budgetDataPoints[period].total += amt;
                        } else {
                            if (!budgetKeys.includes(p)) {
                                budgetDataPoints[period].total += amt;
                            }
                        }
                    });
                }
                currMonth.setMonth(currMonth.getMonth() + 1);
            }

            // Scale daily/weekly/etc budgets if they aren't full months? 
            if (grouping === 'daily' || grouping === 'weekly') {
                budgetDataPoints[period].total = 0;
                budgetDataPoints[period].categories = {};

                let dayIter = new Date(pStart);
                while (dayIter <= pEnd) {
                    const dy = dayIter.getFullYear();
                    const dm = dayIter.getMonth();
                    const dMonthStr = `${dy}-${String(dm + 1).padStart(2, '0')}`;
                    const dMonthStart = new Date(dy, dm, 1);
                    const dMonthEnd = new Date(dy, dm + 1, 0, 23, 59, 59);
                    const daysInMonth = dMonthEnd.getDate();

                    const activeB = sortedBudgets.find(b => {
                        const bs = new Date(b.start_date);
                        const be = b.end_date ? new Date(b.end_date) : null;
                        return bs <= dMonthEnd && (be === null || be >= dMonthStart);
                    });

                    if (activeB) {
                        const rawOverrides = activeB.category_monthly_overrides || {};
                        const rawFixed = activeB.category_amounts || {};
                        const rawFreqs = activeB.category_frequencies || {};

                        const monthlyBudgets = {};
                        const keys = new Set([...Object.keys(rawFixed), ...Object.keys(rawOverrides)]);
                        keys.forEach(k => {
                            if (excludedCategories.includes(k)) return;
                            if (excludeTransfers && (k === 'transfer' || k === 'rewards_cashback')) return;
                            if (excludeDebtPayments && k === 'payments') return;
                            
                            let amt = 0;
                            const overrideVal = rawOverrides[k]?.[dMonthStr];
                            if (overrideVal !== undefined && overrideVal !== null) {
                                amt = parseFloat(overrideVal);
                            } else if (rawFixed[k] !== undefined) {
                                amt = toMonthly(rawFixed[k], rawFreqs[k]);
                            }
                            if (!isNaN(amt)) {
                                monthlyBudgets[k] = amt;
                            }
                        });

                        const allChildren = new Set();
                        const parentToChildren = new Map();
                        
                        if (Array.isArray(categoryHierarchy)) {
                            categoryHierarchy.forEach(p => {
                                if (p.children && Array.isArray(p.children) && p.children.length > 0) {
                                    const kids = p.children.map(ch => ch.value);
                                    parentToChildren.set(p.value, kids);
                                    kids.forEach(k => allChildren.add(k));
                                }
                            });
                        }

                        const budgetKeys = Object.keys(monthlyBudgets);
                        budgetKeys.forEach(k => {
                            const p = childToParent(k);
                            if (p && p !== k) {
                                allChildren.add(k);
                                if (!parentToChildren.has(p)) {
                                    parentToChildren.set(p, []);
                                }
                                if (!parentToChildren.get(p).includes(k)) {
                                    parentToChildren.get(p).push(k);
                                }
                            }
                        });

                        budgetKeys.forEach(k => {
                            const amt = monthlyBudgets[k] || 0;
                            if (amt === 0) return;

                            const p = childToParent(k);
                            let catKey = k;

                            if (showCategories) {
                                if (!showSubCategories) {
                                    if (p) catKey = p;
                                    if (k === 'home_improvement') catKey = 'home';
                                } else {
                                    if (k === 'home_improvement') {
                                        catKey = 'home|home_improvement';
                                    } else if (p && p !== k) {
                                        catKey = `${p}|${k}`;
                                    }
                                }
                                
                                if (!budgetDataPoints[period].categories[catKey]) budgetDataPoints[period].categories[catKey] = 0;
                                budgetDataPoints[period].categories[catKey] += amt / daysInMonth;
                                categoriesSet.add(catKey);
                            }

                            if (!allChildren.has(k)) {
                                budgetDataPoints[period].total += amt / daysInMonth;
                            } else {
                                if (!budgetKeys.includes(p)) {
                                    budgetDataPoints[period].total += amt / daysInMonth;
                                }
                            }
                        });
                    }
                    dayIter.setDate(dayIter.getDate() + 1);
                }
            }
        });

        // Apply Cumulative transformation if requested
        let processedDataPoints = dataPoints;
        let processedBudgetDataPoints = budgetDataPoints;

        if (isCumulative) {
            processedDataPoints = JSON.parse(JSON.stringify(dataPoints));
            processedBudgetDataPoints = JSON.parse(JSON.stringify(budgetDataPoints));

            let runningTotalActual = 0;
            let runningTotalBudget = 0;
            const runningCatActual = {};
            const runningCatBudget = {};

            sortedPeriods.forEach(period => {
                runningTotalActual += processedDataPoints[period].total;
                processedDataPoints[period].total = runningTotalActual;

                Object.keys(processedDataPoints[period].categories).forEach(cat => {
                    runningCatActual[cat] = (runningCatActual[cat] || 0) + processedDataPoints[period].categories[cat];
                    processedDataPoints[period].categories[cat] = runningCatActual[cat];
                });

                runningTotalBudget += (processedBudgetDataPoints[period]?.total || 0);
                if (processedBudgetDataPoints[period]) {
                    processedBudgetDataPoints[period].total = runningTotalBudget;
                    Object.keys(processedBudgetDataPoints[period].categories).forEach(cat => {
                        runningCatBudget[cat] = (runningCatBudget[cat] || 0) + processedBudgetDataPoints[period].categories[cat];
                        processedBudgetDataPoints[period].categories[cat] = runningCatBudget[cat];
                    });
                }
            });
        }

        sortedPeriods = Object.keys(dataPoints).sort();

        // Notify parent of period count for average calculations
        if (onPeriodCountChange) {
            onPeriodCountChange(sortedPeriods.length);
        }

        const datasets = [];

        if (showCategories) {
            Array.from(categoriesSet).forEach(cat => {
                const isSelected = selectedCategory === cat;
                const baseColor = (()=>{
                    let colorKey = cat;
                    if (typeof cat === 'string' && cat.includes('|')) {
                        colorKey = cat.split('|')[0];
                    }
                    return categoryColors[colorKey] || '#616161';
                })();

                datasets.push({
                    label: getCategoryLabel(cat),
                    data: sortedPeriods.map(p => processedDataPoints[p].categories[cat] || 0),
                    backgroundColor: sortedPeriods.map(() => baseColor),
                    borderColor: sortedPeriods.map(() => 'transparent'),
                    borderWidth: sortedPeriods.map(() => 0),
                    stack: 'Actual',
                    categoryValue: cat,
                    order: 2
                });

                datasets.push({
                    label: `${getCategoryLabel(cat)} (Budget)`,
                    data: sortedPeriods.map(p => processedBudgetDataPoints[p]?.categories[cat] || 0),
                    backgroundColor: baseColor + '80', // Semi-transparent
                    borderColor: baseColor,
                    borderWidth: 1,
                    stack: 'Budget',
                    categoryValue: cat,
                    hidden: false,
                    order: 1
                });
            });
        } else {
            datasets.push({
                label: 'Total Expenses',
                data: sortedPeriods.map(p => processedDataPoints[p].total),
                backgroundColor: sortedPeriods.map(() => '#3b82f6'),
                borderColor: sortedPeriods.map(() => '#ffffff'),
                borderWidth: sortedPeriods.map(() => 2),
                stack: 'Actual',
                categoryValue: 'all',
                order: 2
            });

            datasets.push({
                label: 'Total Budget',
                data: sortedPeriods.map(p => processedBudgetDataPoints[p]?.total || 0),
                backgroundColor: 'rgba(16, 185, 129, 0.2)',
                borderColor: '#10b981',
                borderWidth: 2,
                stack: 'Budget',
                categoryValue: 'all',
                order: 1
            });
        }

        // Compute avg actual spend and avg budget across all periods
        const numPeriods = sortedPeriods.length || 1;
        const totalActual = sortedPeriods.reduce((sum, p) => sum + (processedDataPoints[p]?.total || 0), 0);
        const totalBudget = sortedPeriods.reduce((sum, p) => sum + (processedBudgetDataPoints[p]?.total || 0), 0);
        const avgActual = totalActual / numPeriods;
        const avgBudget = totalBudget / numPeriods;

        // Add dashed average spend line dataset
        if (!isCumulative) {
            const avgLineColor = avgActual <= avgBudget ? 'rgba(16, 185, 129, 0.55)' : 'rgba(239, 68, 68, 0.55)';
            datasets.push({
                label: 'Avg Spend',
                data: sortedPeriods.map(() => avgActual),
                type: 'line',
                borderColor: avgLineColor,
                borderWidth: 1.5,
                borderDash: [6, 4],
                pointRadius: 0,
                fill: false,
                tension: 0,
                order: 10,
                stack: undefined,
                _avgActual: avgActual,
                _avgBudget: avgBudget,
            });
        }

        return { labels: sortedPeriods, datasets, dataPoints: processedDataPoints, budgetDataPoints: processedBudgetDataPoints, categoriesSet, avgActual, avgBudget, numPeriods };
    }, [transactions, budgets, grouping, negateForExpenses, enforceExpenseSign, excludeTransfers, excludedCategories, categoryHierarchy, propsStartDate, propsEndDate, showCategories, showSubCategories, onPeriodCountChange, getCategoryLabel, childToParent, isCumulative, categoryColors, excludeDebtPayments]);

    useEffect(() => {
        if (!canvasRef.current) return;
        if (chartRef.current) chartRef.current.destroy();

        chartRef.current = new Chart(canvasRef.current, {
            type: 'bar',
            data: chartData,
            options: {
                responsive: true,
                maintainAspectRatio: false,
                onClick: (event, elements) => {
                    if (elements && elements.length > 0) {
                        const element = elements[0];
                        const index = element.index;
                        const period = chartRef.current?.data?.labels?.[index];
                        if (!period) return;

                        const selectPeriods = onSelectPeriodsRef.current;
                        if (selectPeriods) {
                            const current = Array.isArray(selectedPeriodsRef.current) ? selectedPeriodsRef.current : [];
                            if (current.length === 1 && current[0] === period) {
                                // Clicking the already-selected bar deselects it
                                selectPeriods([]);
                            } else {
                                selectPeriods([period]);
                            }
                        }
                    } else {
                        // Clicked on empty space, clear selection
                        if (onSelectCategoryRef.current) onSelectCategoryRef.current(null);
                        if (onSelectPeriodsRef.current) onSelectPeriodsRef.current([]);
                    }
                },
                plugins: {
                    legend: {
                        display: true,
                        position: 'bottom',
                        onClick: (e, legendItem, legend) => {
                            const selectCategory = onSelectCategoryRef.current;
                            const selectPeriods = onSelectPeriodsRef.current;
                            const curCategory = selectedCategoryRef.current;
                            const curPeriods = selectedPeriodsRef.current;

                            if (legendItem.datasetIndex === -1) {
                                // "All" was clicked
                                if (selectCategory) {
                                    if (curCategory === 'all' || !curCategory) {
                                        selectCategory('none');
                                    } else {
                                        selectCategory('all');
                                    }
                                    if (selectPeriods) selectPeriods([]);
                                }
                                return;
                            }

                            if (!showCategories) {
                                // Standard toggle behavior for Total mode
                                const index = legendItem.datasetIndex;
                                const ci = legend.chart;
                                if (ci.isDatasetVisible(index)) {
                                    ci.hide(index);
                                    legendItem.hidden = true;
                                } else {
                                    ci.show(index);
                                    legendItem.hidden = false;
                                }
                                return;
                            }

                            const index = legendItem.datasetIndex;
                            const dataset = legend.chart.data.datasets[index];
                            const category = dataset.categoryValue;
                            if (selectCategory) {
                                if (curCategory === category && (!curPeriods || curPeriods.length === 0)) {
                                    selectCategory(null);
                                } else {
                                    selectCategory(category);
                                    if (selectPeriods) selectPeriods([]);
                                }
                            }
                        },
                        labels: { 
                            color: '#e5e7eb', 
                            font: { size: 11 },
                            generateLabels: (chart) => {
                                const original = Chart.defaults.plugins.legend.labels.generateLabels(chart);
                                
                                if (!showCategories) {
                                    return original;
                                }

                                // Use ref to always read the latest selectedCategory (avoids stale closure)
                                const curCat = selectedCategoryRef.current;

                                // Add "All" option at the beginning
                                const allLabel = {
                                    text: (curCat === 'all' || !curCat) ? '✓ All Categories' : (curCat === 'none' ? '✗ All Categories' : 'All Categories'),
                                    fillStyle: (curCat === 'all' || !curCat) ? '#3b82f6' : '#1f2937',
                                    strokeStyle: '#3b82f6',
                                    lineWidth: 1,
                                    hidden: false,
                                    index: -1, // Special index for All
                                    datasetIndex: -1,
                                    fontColor: (curCat === 'all' || !curCat) ? '#ffffff' : (curCat === 'none' ? '#6b7280' : '#9ca3af')
                                };

                                const processed = original.map(label => {
                                    const dataset = chart.data.datasets[label.datasetIndex];
                                    if (dataset.categoryValue === curCat) {
                                        label.text = '✓ ' + label.text;
                                        label.fontColor = '#ffffff';
                                    } else if (curCat && curCat !== 'all' && curCat !== 'none') {
                                        label.fontColor = '#9ca3af';
                                    } else if (curCat === 'none') {
                                        label.fontColor = '#6b7280'; // Even darker when none selected
                                    }
                                    return label;
                                });

                                return [allLabel, ...processed];
                            }
                        }
                    },
                    tooltip: {
                        backgroundColor: '#1f2937',
                        titleColor: '#e5e7eb',
                        bodyColor: '#e5e7eb',
                        callbacks: {
                            label: (context) => {
                                let label = context.dataset.label || '';
                                if (label) label += ': ';
                                if (context.parsed.y !== null) {
                                    label += new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(context.parsed.y);
                                }
                                return label;
                            }
                        }
                    }
                },
                scales: {
                    x: {
                        stacked: true,
                        ticks: { color: '#9ca3af' },
                        grid: { color: '#374151' }
                    },
                    y: {
                        grace: '5%',
                        stacked: false,
                        ticks: { 
                            color: '#9ca3af',
                            callback: (value) => '$' + value.toLocaleString()
                        },
                        grid: { color: '#374151' }
                    }
                },
                animation: {
                    onProgress: function(animation) {
                        renderDiffLabels(animation.chart);
                    },
                    onComplete: function(animation) {
                        renderDiffLabels(animation.chart);
                    }
                }
            }
        });

        return () => {
            if (chartRef.current) chartRef.current.destroy();
        };
    }, [chartData, showCategories, onSelectCategory, onSelectPeriods]);

    // Update bar colors in-place when selection changes — avoids destroying/recreating the chart
    useEffect(() => {
        const chart = chartRef.current;
        if (!chart) return;

        const labels = chart.data.labels || [];
        const hasPeriodSelection = Array.isArray(selectedPeriods) && selectedPeriods.length > 0;

        chart.data.datasets.forEach((dataset) => {
            const cat = dataset.categoryValue;
            if (!cat) return;

            if (dataset.stack === 'Budget') return; // leave budget bars unchanged

            if (!showCategories) {
                // Total Expenses dataset
                dataset.backgroundColor = labels.map(p => {
                    if (hasPeriodSelection) return selectedPeriods.includes(p) ? '#3b82f6' : 'rgba(59, 130, 246, 0.2)';
                    if (selectedCategory && selectedCategory !== 'all' && selectedCategory !== 'none') return 'rgba(59, 130, 246, 0.4)';
                    if (selectedCategory === 'none') return 'rgba(59, 130, 246, 0.1)';
                    return '#3b82f6';
                });
                dataset.borderColor = labels.map(p => {
                    if (hasPeriodSelection) return selectedPeriods.includes(p) ? '#ffffff' : 'transparent';
                    return (selectedCategory === 'all' || !selectedCategory) ? '#ffffff' : 'transparent';
                });
                dataset.borderWidth = labels.map(p => {
                    if (hasPeriodSelection) return selectedPeriods.includes(p) ? 2 : 0;
                    return (selectedCategory === 'all' || !selectedCategory) ? 2 : 0;
                });
            } else {
                // Category datasets — derive base color from first backgroundColor value
                const baseColor = Array.isArray(dataset.backgroundColor)
                    ? (dataset.backgroundColor[0] || '#616161').replace(/30$/, '').replace(/[0-9a-f]{2}$/, s => s.length === 2 ? '' : s)
                    : (dataset.backgroundColor || '#616161');
                // Re-derive from categoryColors using categoryValue
                let colorKey = cat;
                if (typeof cat === 'string' && cat.includes('|')) colorKey = cat.split('|')[0];
                const bc = categoryColors[colorKey] || '#616161';

                const catDimmedAll = selectedCategory === 'none';
                const catDimmed = catDimmedAll || (selectedCategory && selectedCategory !== 'all' && selectedCategory !== cat);

                dataset.backgroundColor = labels.map(p => {
                    const periodDimmed = hasPeriodSelection && !selectedPeriods.includes(p);
                    if (catDimmed || periodDimmed) return bc + '30';
                    return bc;
                });
                dataset.borderColor = labels.map(p => {
                    if (hasPeriodSelection && selectedPeriods.includes(p) && selectedCategory === cat) return '#ffffff';
                    if (hasPeriodSelection && selectedPeriods.includes(p)) return 'rgba(255,255,255,0.6)';
                    return 'transparent';
                });
                dataset.borderWidth = labels.map(p => (hasPeriodSelection && selectedPeriods.includes(p)) ? 2 : 0);
            }
        });

        chart.update('none'); // 'none' = no animation, instant color update
    }, [selectedPeriods, selectedCategory, showCategories, categoryColors]);

    return (
        <div className="mb-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <div className="d-flex align-items-center gap-3">
                    {selectedCategory && (
                        <div className="badge bg-primary d-flex align-items-center gap-2" style={{ fontSize: '0.9rem', padding: '8px 12px' }}>
                            <span>
                                {selectedCategory === 'all' ? 'All Categories' : (selectedCategory === 'none' ? 'None Selected' : getCategoryLabel(selectedCategory))}
                                {selectedPeriods && selectedPeriods.length > 0 && ` (${selectedPeriods.join(', ')})`}
                            </span>
                            <button 
                                type="button" 
                                className="btn-close btn-close-white" 
                                style={{ fontSize: '0.5rem' }}
                                onClick={() => {
                                    if (onSelectCategory) onSelectCategory(null);
                                    if (onSelectPeriods) onSelectPeriods([]);
                                }}
                            ></button>
                        </div>
                    )}
                    <h4 className="mb-0">Expense Time Series</h4>
                    <select 
                        className="form-select form-select-sm" 
                        style={{ width: 'auto', backgroundColor: '#1f2937', color: '#e5e7eb', border: '1px solid #374151' }}
                        value={grouping}
                        onChange={(e) => setGrouping(e.target.value)}
                    >
                        <option value="daily">Daily</option>
                        <option value="weekly">Weekly</option>
                        <option value="monthly">Monthly</option>
                        <option value="quarterly">Quarterly</option>
                        <option value="annually">Annually</option>
                    </select>
                </div>
                <div className="d-flex align-items-center gap-3">
                    <div className="form-check form-switch" style={{ display:'flex', alignItems:'center', gap:6 }}>
                        <input 
                            className="form-check-input" 
                            type="checkbox" 
                            id="excludeDebtPaymentsTimeSeries" 
                            checked={excludeDebtPayments} 
                            onChange={(e)=> {
                                setExcludeDebtPayments(e.target.checked);
                                if (onExcludeDebtPaymentsChange) onExcludeDebtPaymentsChange(e.target.checked);
                            }} 
                        />
                        <label className="form-check-label" htmlFor="excludeDebtPaymentsTimeSeries">Exclude Debt Payments</label>
                    </div>
                    <div className="form-check form-switch" style={{ display:'flex', alignItems:'center', gap:6 }}>
                        <input 
                            className="form-check-input" 
                            type="checkbox" 
                            id="isCumulativeTimeSeries" 
                            checked={isCumulative} 
                            onChange={(e)=> setIsCumulative(e.target.checked)} 
                        />
                        <label className="form-check-label" htmlFor="isCumulativeTimeSeries">Cumulative</label>
                    </div>
                    <div className="form-check form-switch" style={{ display:'flex', alignItems:'center', gap:6 }}>
                        <input 
                            className="form-check-input" 
                            type="checkbox" 
                            id="showCatTimeSeries" 
                            checked={showCategories} 
                            onChange={(e)=> setShowCategories(e.target.checked)} 
                        />
                        <label className="form-check-label" htmlFor="showCatTimeSeries">Show by categories</label>
                    </div>
                    {showCategories && (
                        <div className="form-check form-switch" style={{ display:'flex', alignItems:'center', gap:6 }}>
                            <input 
                                className="form-check-input" 
                                type="checkbox" 
                                id="showSubCatTimeSeries" 
                                checked={showSubCategories} 
                                onChange={(e)=> setShowSubCategories(e.target.checked)} 
                            />
                            <label className="form-check-label" htmlFor="showSubCatTimeSeries">Show sub-categories</label>
                        </div>
                    )}
                </div>
            </div>

            {/* Stats bar: avg budget, avg spend, difference */}
            {!isCumulative && chartData.numPeriods > 0 && (() => {
                const fmt = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(v);
                const diff = chartData.avgActual - chartData.avgBudget;
                const periodLabel = grouping === 'daily' ? 'Daily' : grouping === 'weekly' ? 'Weekly' : grouping === 'monthly' ? 'Monthly' : grouping === 'quarterly' ? 'Quarterly' : 'Annual';
                return (
                    <div className="d-flex gap-4 mb-3" style={{ fontSize: '0.9rem' }}>
                        <div style={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px', padding: '10px 18px', textAlign: 'center' }}>
                            <div style={{ color: '#9ca3af', fontSize: '0.75rem', marginBottom: 2 }}>Avg {periodLabel} Budget</div>
                            <div style={{ color: '#10b981', fontWeight: 600, fontSize: '1.05rem' }}>{fmt(chartData.avgBudget)}</div>
                        </div>
                        <div style={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px', padding: '10px 18px', textAlign: 'center' }}>
                            <div style={{ color: '#9ca3af', fontSize: '0.75rem', marginBottom: 2 }}>Avg {periodLabel} Spend</div>
                            <div style={{ color: '#ef4444', fontWeight: 600, fontSize: '1.05rem' }}>{fmt(chartData.avgActual)}</div>
                        </div>
                        <div style={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px', padding: '10px 18px', textAlign: 'center' }}>
                            <div style={{ color: '#9ca3af', fontSize: '0.75rem', marginBottom: 2 }}>{diff >= 0 ? 'Over Budget' : 'Under Budget'} (avg)</div>
                            <div style={{ color: diff >= 0 ? '#ef4444' : '#10b981', fontWeight: 600, fontSize: '1.05rem' }}>{diff >= 0 ? '+' : ''}{fmt(diff)}</div>
                        </div>
                    </div>
                );
            })()}
            <div className="chart-card" style={{ height: '460px', position: 'relative', backgroundColor: '#111827', padding: '20px', borderRadius: '8px', border: '1px solid #374151' }}>
                <canvas ref={canvasRef}></canvas>
            </div>
        </div>
    );
};

export default ExpensesTimeSeriesChart;
